import { useEffect, useState } from 'react';
import MarketplaceTopNavigation from '@/components/MarketplaceTopNavigation';
import BottomNavigation from '@/components/BottomNavigation';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tag, Percent, ShoppingBag, Sparkles, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

interface Promo {
  id: string;
  code: string;
  description: string | null;
  discount_type: string;
  discount_value: number;
  start_date: string;
  end_date: string;
  business_type: string;
  min_order_amount: number | null;
  max_discount: number | null;
  restaurants: { id: string; name: string; business_type: string; image_url: string | null } | null;
}

const MarketplaceDealsPage = () => {
  const [promos, setPromos] = useState<Promo[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyPromoCode = async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      toast.success(`Promo code "${code}" copied!`);
      setTimeout(() => setCopiedCode(null), 2000);
    } catch (error) {
      toast.error('Failed to copy promo code');
    }
  };

  useEffect(() => {
    const fetchPromos = async () => {
      try {
        const { data, error } = await supabase
          .from('promos')
          .select(`
            *,
            restaurants(id, name, business_type, image_url)
          `)
          .eq('is_active', true)
          .eq('is_public', true)
          .lte('start_date', new Date().toISOString())
          .gte('end_date', new Date().toISOString())
          .order('start_date', { ascending: false });
        
        if (error) throw error;
        
        // Filter for marketplace business type (business_type field exists in DB but not in types yet)
        const marketplacePromos = (data || []).filter((promo: any) => 
          promo.business_type === 'marketplace' || !promo.business_type
        );
        setPromos(marketplacePromos as any);
      } catch (e) {
        console.error('Error fetching marketplace promos', e);
      } finally {
        setLoading(false);
      }
    };
    fetchPromos();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <MarketplaceTopNavigation />
      <div className="px-4 py-6 pb-24">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-primary/70 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Sparkles className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Marketplace Deals</h1>
          <p className="text-muted-foreground">Save big on your favorite products</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
          </div>
        ) : promos.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <ShoppingBag className="h-10 w-10 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-lg">No active marketplace deals right now</p>
            <p className="text-muted-foreground/70 text-sm mt-2">Check back soon for amazing offers!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {promos.map((promo) => (
              <Card key={promo.id} className="overflow-hidden shadow-md hover:shadow-lg transition-shadow border-border animate-fade-in hover-scale">
                <div className="relative">
                  {/* Gradient Header */}
                  <div className="bg-gradient-to-r from-primary to-primary/80 p-5 text-white animate-enter">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                          <Percent className="h-7 w-7" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="secondary" className="bg-white text-primary border-transparent font-mono text-sm px-3 py-1">
                              {promo.code}
                            </Badge>
                          </div>
                          <h3 className="text-2xl font-bold">
                            {promo.discount_type === 'percentage' 
                              ? `${promo.discount_value}% OFF` 
                              : `D${promo.discount_value} OFF`}
                          </h3>
                        </div>
                      </div>
                      {promo.restaurants?.image_url && (
                        <img
                          src={promo.restaurants.image_url}
                          alt={promo.restaurants.name}
                          className="w-16 h-16 rounded-full object-cover border-2 border-white/30"
                        />
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <CardContent className="p-5">
                    {promo.description && (
                      <p className="text-foreground mb-4 leading-relaxed">{promo.description}</p>
                    )}
                    
                    <div className="bg-muted/50 rounded-lg p-4 space-y-2 mb-4">
                      {promo.restaurants && (
                        <div className="flex items-center gap-2">
                          <Tag className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-foreground">Valid at <span className="font-semibold">{promo.restaurants.name}</span></span>
                        </div>
                      )}
                      {promo.min_order_amount && (
                        <div className="flex items-center gap-2">
                          <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-foreground">Minimum order: <span className="font-semibold">D{promo.min_order_amount}</span></span>
                        </div>
                      )}
                      {!promo.restaurants && !promo.min_order_amount && (
                        <div className="flex items-center gap-2">
                          <Sparkles className="h-4 w-4 text-primary" />
                          <span className="text-sm text-foreground font-semibold">Valid on all marketplace purchases</span>
                        </div>
                      )}
                    </div>

                    <Button
                      onClick={() => copyPromoCode(promo.code)}
                      variant="secondary"
                      className="w-full hover-scale"
                      size="lg"
                    >
                      {copiedCode === promo.code ? (
                        <>
                          <Check className="h-5 w-5 mr-2" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-5 w-5 mr-2" />
                          Copy Code: {promo.code}
                        </>
                      )}
                    </Button>
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <BottomNavigation />
    </div>
  );
};

export default MarketplaceDealsPage;
